import {inject, Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {SupabaseAuthService} from './supabase-auth.service';
import {V4HttpClient} from './v4-http.client';

export interface AccountSession {
  id: string;
  aal: string;
  created_at: string;
  updated_at: string;
  refreshed_at: string | null;
  factor_id: string | null;
  not_after: string | null;
  user_agent: string | null;
  ip: string | null;
  current: boolean;
}

export interface AvatarUploadSignature {
  access_id: string;
  policy: string;
  signature: string;
  prefix: string;
  host: string;
  expire: number;
  callback?: string;
}

const CLIENT_ID_KEY = 'mp-auth-client-id';

/**
 * Account API client (mp-api /v4/account/*).
 * Browser cannot list auth.sessions via supabase-js; must go through mp-api.
 */
@Injectable({
  providedIn: 'root',
})
export class AccountApiService {
  private readonly v4Http = inject(V4HttpClient);
  private readonly supabaseAuth = inject(SupabaseAuthService);

  private get baseUrl(): string {
    return this.v4Http.publicAccountBaseUrl;
  }

  async getSessions(): Promise<AccountSession[]> {
    const response = await this.v4Http.post<AccountSession[]>(
      `${this.baseUrl}/sessions/list`,
      {},
      await this.authHeaders(),
    );
    if (response.code) {
      throw new Error(response.message || 'Failed to load sessions.');
    }
    return Array.isArray(response.result) ? response.result : [];
  }

  async revokeSession(sessionId: string): Promise<void> {
    const response = await this.v4Http.post<null>(
      `${this.baseUrl}/sessions/revoke`,
      {session_id: sessionId},
      await this.authHeaders(),
    );
    if (response.code) {
      throw new Error(response.message || 'Failed to revoke session.');
    }
  }

  async getCaptcha(params: {username: string; action: string}): Promise<void> {
    const response = await this.v4Http.post<null>(`${this.baseUrl}/get-captcha`, {
      username: params.username,
      action: params.action,
      client_id: this.getClientId(),
    });
    if (response.code) {
      throw new Error(response.message || 'Failed to send verification code.');
    }
  }

  async issueSecurityVerification(params: {
    action: string;
    resource: Record<string, unknown>;
    method: 'email_otp' | 'mfa';
    code?: string;
  }): Promise<{ticket: string; expires_at: string}> {
    const response = await this.v4Http.post<{ticket: string; expires_at: string}>(
      `${this.baseUrl}/security-verification/issue`,
      params,
      await this.authHeaders(),
    );
    if (response.code || !response.result?.ticket) {
      throw new Error(response.message || 'Security verification failed.');
    }
    return response.result;
  }

  async authorizeSecurityAddition(
    type: 'mfa' | 'passkey',
    params: {user_id: string},
    verificationTicket: string,
  ): Promise<void> {
    const response = await this.v4Http.post<null>(
      `${this.baseUrl}/${type}/add/authorize`,
      params,
      await this.authHeaders({'X-Security-Verification': verificationTicket}),
    );
    if (response.code) {
      throw new Error(response.message || `Failed to authorize ${type} addition.`);
    }
  }

  async removeMfaFactor(
    params: {user_id: string; factor_id: string},
    verificationTicket: string,
  ): Promise<void> {
    const response = await this.v4Http.post<null>(
      `${this.baseUrl}/mfa/remove`,
      params,
      await this.authHeaders({'X-Security-Verification': verificationTicket}),
    );
    if (response.code) {
      throw new Error(response.message || 'Failed to remove MFA factor.');
    }
  }

  async removePasskey(
    params: {user_id: string; passkey_id: string},
    verificationTicket: string,
  ): Promise<void> {
    const response = await this.v4Http.post<null>(
      `${this.baseUrl}/passkey/remove`,
      params,
      await this.authHeaders({'X-Security-Verification': verificationTicket}),
    );
    if (response.code) {
      throw new Error(response.message || 'Failed to remove passkey.');
    }
  }

  async deleteAccount(
    params: {user_id: string},
    verificationTicket: string,
  ): Promise<void> {
    const response = await this.v4Http.post<null>(
      `${this.baseUrl}/delete`,
      params,
      await this.authHeaders({'X-Security-Verification': verificationTicket}),
    );
    if (response.code) {
      throw new Error(response.message || 'Failed to delete account.');
    }
  }

  async getAvatarUploadSignature(): Promise<AvatarUploadSignature> {
    const response = await this.v4Http.post<AvatarUploadSignature>(
      `${this.baseUrl}/avatar/upload-signature`,
      {},
      await this.authHeaders(),
    );
    if (response.code || !response.result?.host || !response.result?.prefix) {
      throw new Error(response.message || 'Failed to get avatar upload signature.');
    }
    return response.result;
  }

  async commitAvatar(tempPath: string): Promise<{avatar_url: string}> {
    const response = await this.v4Http.post<{avatar_url: string}>(
      `${this.baseUrl}/avatar/commit`,
      {temp_path: tempPath},
      await this.authHeaders(),
    );
    if (response.code || !response.result?.avatar_url) {
      throw new Error(response.message || 'Failed to commit avatar.');
    }
    return response.result;
  }

  private getClientId(): string {
    try {
      const existing = localStorage.getItem(CLIENT_ID_KEY)?.trim();
      if (existing) {
        return existing;
      }
      const created = crypto.randomUUID();
      localStorage.setItem(CLIENT_ID_KEY, created);
      return created;
    } catch {
      return crypto.randomUUID();
    }
  }

  private async authHeaders(extra: Record<string, string> = {}): Promise<HttpHeaders> {
    const token = await this.supabaseAuth.getAccessToken();
    if (!token) {
      throw new Error('Not signed in.');
    }
    return new HttpHeaders({Authorization: `Bearer ${token}`, ...extra});
  }
}
